import { parsearFechaCaducidad, acumularProducto, aNumero } from "./comun";

/**
 * Parsea las líneas de texto extraídas de un PDF de Laboratorios PISA (RFC: LPI830527KJ2).
 * Retorna un Map<clave, { lotes, cantidadLinea }>, donde clave es la descripción o el
 * código del proveedor (ambos apuntan a la misma entrada).
 *
 * Maqueta de la fila de producto:
 *   LINEA | CLAVE/COD | CVE PROD | CVE UNIDAD | OBJ IMPUESTO | DESCRIPCION | LOTE |
 *   CANTIDAD | UDM | PRECIO | IMPORTE
 *
 * Ejemplo real (factura 64182507):
 *   1 4000171 51101600 H87 Sí objeto de OTROZOL 500 5MG/ML C/100ML FLX SI V26M518 5.000 PZA 35.97 179.85
 *   impuesto Fecha Caducidad: 2028-03-01
 *
 * Dos detalles de la maqueta que obligan al diseño:
 *  - "Sí objeto de impuesto" se parte en dos filas visuales, así que la palabra suelta
 *    puede aparecer o no en la fila del producto: se limpia de la descripción en vez de
 *    exigirla en el regex.
 *  - La caducidad va en una fila de continuación. Se quita de la fila si vino pegada y,
 *    si no, se busca en las siguientes (sin invadir el producto que sigue).
 */

// LINEA CODIGO CVEPROD(8) CVEUNIDAD ... DESCRIPCION LOTE CANTIDAD UDM PRECIO IMPORTE
const PRODUCT_LINE =
  /^(\d{1,3})\s+(\S+)\s+(\d{8})\s+([A-Z0-9]{2,4})\s+(.+?)\s+(\S+)\s+(\d+(?:[.,]\d+)?)\s+([A-Za-z]{2,5})\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})\s*$/i;

const CADUCIDAD = /Fecha\s+Caducidad:?\s*(\d{4}-\d{2}-\d{2})/i;

// Sobrantes de la columna "OBJ IMPUESTO" que se cuelan al inicio de la descripción
const OBJ_IMPUESTO = /^(?:impuesto\s+)?(?:S[ií]|No)\s+objeto\s+de(?:\s+impuesto)?\s*/i;

const LINEAS_LOOKAHEAD = 2;

/** Saca la caducidad de una fila y devuelve la fila sin ella. */
function extraerCaducidad(linea) {
  const match = linea.match(CADUCIDAD);
  if (!match) return { texto: linea, caducidad: null };
  return {
    texto: linea.replace(CADUCIDAD, " ").replace(/\s+/g, " ").trim(),
    caducidad: parsearFechaCaducidad(match[1]),
  };
}

export function parsearLineasPisa(allLines) {
  const lineas = (allLines || []).map((l) => String(l ?? "").replace(/\s+/g, " ").trim());
  const productos = [];

  for (let i = 0; i < lineas.length; i++) {
    const { texto, caducidad } = extraerCaducidad(lineas[i]);
    const match = texto.match(PRODUCT_LINE);
    if (!match) continue;

    const descripcion = match[5].replace(OBJ_IMPUESTO, "").trim();
    if (!descripcion) continue;

    // La caducidad suele venir en la fila de continuación, no en la del producto
    let fecha = caducidad;
    for (let j = i + 1; !fecha && j <= i + LINEAS_LOOKAHEAD && j < lineas.length; j++) {
      // No robarle la caducidad al producto siguiente
      if (PRODUCT_LINE.test(extraerCaducidad(lineas[j]).texto)) break;
      fecha = extraerCaducidad(lineas[j]).caducidad;
    }

    productos.push({
      codigo: match[2],
      descripcion,
      lote: match[6],
      cantidad: aNumero(match[7]),
      caducidad: fecha,
    });
  }

  const productsByDesc = new Map();
  for (const prod of productos) acumularProducto(productsByDesc, prod);
  return productsByDesc;
}

export default parsearLineasPisa;
